import React, { forwardRef, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CustomModal, BuySellDetailsMRT } from '@components'
import { showToast, getMargin } from '@utils';
import {
    Box,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Typography
} from '@mui/material';
import {
    apiBuyContract,
    apiBuyTheEurContractMinMax,
    apiGetMinMaxRaw,
    apiGetTheEur,
    apiGetMinMaxDisplay,
    apiGetOrder
} from '@store';

export const Buy = forwardRef(
    ({ handleClose, selectedContract, user, sym }, ref) => {
        const dispatch = useDispatch();
        const [submitting, setSubmitting] = useState(false);
        const [showMinMaxInput, setShowMinMaxInput] = useState(false);
        const { hedgeMargin } = useSelector((state) => state.hedgeMargin);
        const { isBuyLoading } = useSelector((state) => state.the);

        const [state, setState] = useState({
            sym: selectedContract?.contract || sym,
            starthour: user?.StartTime,
            endhour: user?.EndTime,
            lunchstart: user?.LunchStart,
            lunchend: user?.LunchEnd,
            Hideflex: user?.Hideflex,
            Company: user?.Company,
            CompanyShort: user?.CompanyShort,
            UserCanBuyFrom: user?.CanBuyFrom,
            UserCanSellTo: user?.CanSellTo,
            Endpoints: user?.Endpoints,
            Currency: 'EUR',
            market: 'THE',
            type: 'buy',
            price_selected: '',
            seller: '',
            buyer: user?.CompanyShort,
            deliverypoint: 'THE',
            price_percent: '100',
            volume: '',
            exitpoint: '',
            ordertype: 'internal',
            currency: 'EUR',
            max_price: '',
            on_max: false,
            min_price: '',
            on_min: false
        });

        const [margin, setMargin] = useState(0);

        useEffect(() => {
            setMargin(
                getMargin({
                    contractName: selectedContract?.contract || sym,
                    hedgeMargin,
                    volume: state?.volume
                })
            );
        }, [selectedContract, sym, hedgeMargin, state?.volume]);

        useEffect(() => {
            if (user?.CanBuyFrom?.length > 0) {
                setState((prevState) => ({
                    ...prevState,
                    seller: user.CanBuyFrom[0]
                }));
            }
        }, [user]);

        const handleChange = (e) => {
            const { name, value } = e.target;
            setState({
                ...state,
                [name]: value
            });
        };

        const [type, setType] = useState('ask');

        const handleTypeChange = (e) => {
            setType(e.target.value);
            if (e.target.value === 'min_max') {
                setShowMinMaxInput(true);
            } else if (e.target.value === 'ask') {
                setShowMinMaxInput(false);
            }
        };

        const handleMinChange = (e) => {
            // If min price is changed, clear max price and disable it
            // If min price is cleared, enable max price
            if (e.target.value === '') {
                setState({
                    ...state,
                    min_price: e.target.value,
                    max_price: '',
                    on_min: false,
                    on_max: false
                });
            } else {
                setState({
                    ...state,
                    min_price: e.target.value,
                    max_price: '',
                    on_min: true,
                    on_max: false
                });
            }
        };

        const handleMaxChange = (e) => {
            // If max price is changed, clear min price and disable it
            // If max price is cleared, enable min price
            if (e.target.value === '') {
                setState({
                    ...state,
                    max_price: e.target.value,
                    min_price: '',
                    on_max: false,
                    on_min: false
                });
            } else {
                setState({
                    ...state,
                    max_price: e.target.value,
                    min_price: '',
                    on_max: true,
                    on_min: false
                });
            }
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (submitting) return;
            setSubmitting(true);
            let data;
            if (type === 'ask') {
                // Remove min_max fields
                const { min_price, max_price, on_min, on_max, ...rest } = state;
                const volume = parseFloat(state.volume);
                const ask = parseFloat(selectedContract.ask);
                const bid = parseFloat(selectedContract.bid);
                const updated_at = selectedContract?.updated_at;
                const buyback10 = parseFloat(selectedContract.buyback10);
                const buyback20 = parseFloat(selectedContract.buyback20);
                const buyback30 = parseFloat(selectedContract.buyback30);

                const data = {
                    ...rest,
                    margin: margin,
                    total_price: ask + margin,
                    volume,
                    exitpoint: state.exitpoint || {},
                    price: {
                        ask: ask,
                        bid: bid,
                        updated_at: updated_at,
                        buyback10: buyback10,
                        buyback20: buyback20,
                        buyback30: buyback30
                    }
                };

                const resp = await dispatch(apiBuyContract(data));
                if (resp?.meta?.requestStatus === 'fulfilled') {
                    handleClose();
                    dispatch(apiGetOrder());
                    showToast('success', 'Purchase Order Saved');
                } else if (resp?.meta?.requestStatus === 'rejected') {
                    showToast(
                        'error',
                        resp?.payload || 'Error buying contract'
                    );
                }
            } else if (type === 'min_max') {
                const minprice = parseFloat(state.min_price);
                const maxprice = parseFloat(state.max_price);

                data = {
                    ...state,
                    margin: margin,
                    total_price: selectedContract?.ask + margin,
                    min_price: minprice || 0,
                    max_price: maxprice || 0,
                    on_min: minprice ? true : false,
                    on_max: maxprice ? true : false,
                    exitpoint: state.exitpoint || {}
                };

                const resp = await dispatch(apiBuyTheEurContractMinMax(data));
                if (resp?.meta?.requestStatus === 'fulfilled') {
                    handleClose();
                    dispatch(apiGetMinMaxRaw());
                    dispatch(apiGetMinMaxDisplay());
                    dispatch(apiGetTheEur());
                    dispatch(apiGetOrder());
                    showToast('success', 'Purchase Order Saved');
                } else if (resp?.meta?.requestStatus === 'rejected') {
                    showToast(
                        'error',
                        resp?.payload || 'Error buying contract'
                    );
                }
            }
            setSubmitting(false);
        };

        const [timer, setTimer] = useState(25);

        useEffect(() => {
            const interval = setInterval(() => {
                setTimer((timer) => timer - 1);
            }, 1000);
            return () => clearInterval(interval);
        }, [timer]);

        useEffect(() => {
            if (timer === 0) {
                handleClose();
            }
        }, [timer, handleClose]);

        useEffect(() => {
            if (ref?.current) {
                setTimer(25);
            }
        }, [ref]);

        return (
            <CustomModal
                handleClose={handleClose}
                handleSubmit={handleSubmit}
                modalTitle={`Buy Contract`}
                buttonText={'Buy'}
                timer={timer}
                disabled={submitting || (state.volume ? false : true)}
                ref={ref}
            >
                <Box className="flex flex-col gap-3">
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                            shrink
                        >
                            Please Select what you would like to buy
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Type"
                            name="type"
                            value={type}
                            onChange={handleTypeChange}
                        >
                            <MenuItem value="ask">Ask Price</MenuItem>
                            <MenuItem value="min_max">Min/Max Stop</MenuItem>
                        </Select>
                    </FormControl>
                    <Box className="flex gap-3">
                        <Typography>Contract:</Typography>
                        <Typography color={'black'}>
                            {selectedContract?.contract || sym}
                        </Typography>
                    </Box>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel
                            id="contractLabel"
                            sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                        >
                            Volume
                        </InputLabel>
                        <OutlinedInput
                            id="volume"
                            label="Volume"
                            name="volume"
                            onChange={handleChange}
                            value={state.volume}
                            type="number"
                            inputProps={{
                                step: 0.01,
                                min: 0
                            }}
                            required
                            endAdornment={
                                <InputAdornment position="start" sx={{ ml: 2 }}>
                                    MW
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Endpoint
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Endpoint"
                            name="exitpoint"
                            value={state.exitpoint}
                            onChange={handleChange}
                        >
                            {user?.Endpoints?.map((endpoint, index) => (
                                <MenuItem key={index} value={endpoint}>
                                    {endpoint.name} - {endpoint.location}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Seller
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Seller"
                            name="seller"
                            value={state.seller}
                            onChange={handleChange}
                        >
                            {user?.CanBuyFrom?.map((seller, index) => (
                                <MenuItem key={index} value={seller}>
                                    {seller}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth variant="outlined" disabled>
                        <InputLabel
                            id="deliverypointLabel"
                            sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                        >
                            Delivery Point
                        </InputLabel>
                        <OutlinedInput
                            id="deliverypoint"
                            label="Delivery Point"
                            name="deliverypoint"
                            value={state.deliverypoint || 'THE'}
                        />
                    </FormControl>
                    <BuySellDetailsMRT
                        price={selectedContract?.ask}
                        margin={margin || 0}
                        totalPrice={(margin + parseFloat(selectedContract?.ask)).toFixed(3)}
                        currency="EUR"
                        deliveryPoint={state.deliverypoint}
                        contractHours={selectedContract?.contract_hours}
                        notionalValue={state?.volume ? (selectedContract?.contract_hours * (parseFloat(selectedContract?.ask) + margin) * parseFloat(state.volume)).toFixed(3) : 0}
                        lastUpdated={selectedContract?.updated_at_cet_str}
                    />
                    {showMinMaxInput && (
                        <Box className="flex gap-3">
                            <FormControl fullWidth variant="outlined">
                                <InputLabel
                                    id="priceLabel"
                                    sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                                >
                                    Max Price
                                </InputLabel>
                                <OutlinedInput
                                    id="price"
                                    label="Price"
                                    name="maxprice"
                                    value={state.maxprice}
                                    onChange={handleMaxChange}
                                    disabled={state.on_min}
                                    required
                                />
                            </FormControl>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel
                                    id="priceLabel"
                                    sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                                >
                                    Min Price
                                </InputLabel>
                                <OutlinedInput
                                    id="price"
                                    label="Price"
                                    name="minprice"
                                    value={state.minprice}
                                    onChange={handleMinChange}
                                    disabled={state.on_max}
                                    required
                                />
                            </FormControl>
                        </Box>
                    )}
                </Box>
            </CustomModal>
        );
    }
);
